import React, { useState, useEffect } from 'react';
import { Button, ErrorMessage, FlexRow, Grid, Header4Text, ModalWindow, PageLoading } from 'ui-toolkit';
import { useGetChecksIncomingActualQuery, usePostMatchChecksIncomingMutation } from 'API/accounting/AccountingApi';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { MatchActualCheckDepositTable } from './MatchActualCheckDepositTable';
import { IEscrowActualIncomingCheckData } from 'types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowActualIncomingCheckQuery } from 'types/accounting/query/IEscrowActualIncomingCheckQuery';
import { createDateRangeFromDays } from 'utilities/accounting/createDateRangeFromDays';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { convertToError } from 'API/BaseApi';
import { ePayerType } from 'types/order/escrow/ePayerType';
import { eDepositCategory } from 'types/order/escrow/eDepositCategory';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

export interface IMatchingCheckModalSectionProps {
  expectedCheck: { amountCents: number; payerType: ePayerType; category: eDepositCategory; id: string };
  onClose: () => void;
}

export const MatchingCheckModalSection: React.FC<IMatchingCheckModalSectionProps> = ({ onClose, expectedCheck }) => {
  const [selectedCheck, setSelectedCheck] = useState<IEscrowActualIncomingCheckData | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const getDefaultCheckQuery: () => IEscrowActualIncomingCheckQuery = () => ({
    amountCents: expectedCheck.amountCents,
    amountCentsMin: expectedCheck.amountCents,
    completedAtDate: createDateRangeFromDays(-60),
    matched: eMatchedQueryValue.UNMATCHED,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'completedAtDate',
    sortOrder: 'desc',
    wellsFargoPaymentManagerReportType: 'PreviousDay',
  });

  const [queryObject, setQueryObject] = useState(getDefaultCheckQuery);

  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingChecksRead]);
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualIncomingChecksRead]);
  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualIncomingChecksEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingChecksEdit]);

  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const {
    data: getActualChecks,
    isLoading: isLoadingGetActualChecks,
    error: getActualChecksError,
  } = useGetChecksIncomingActualQuery(queryObject, {
    skip,
  });

  useEffect(() => {
    const newSelectedCheck = selectedId && getActualChecks && getActualChecks.content.find((i) => i.id === selectedId);
    if (newSelectedCheck && ((selectedCheck && newSelectedCheck.id !== selectedCheck.id) || !selectedCheck)) {
      setSelectedCheck(newSelectedCheck);
    }
  }, [getActualChecks, selectedCheck, selectedId]);

  const [doMatch, { error: matchError }] = usePostMatchChecksIncomingMutation();
  const onMatch = async () => {
    if (!selectedCheck || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selectedCheck.id, expectedId: expectedCheck.id });

    if (checkForMutationSuccess(result, 'matching check')) {
      onClose();
    }
  };

  const isQueryValid =
    !queryObject.amountCents || !queryObject.amountCentsMin || queryObject.amountCents >= queryObject.amountCentsMin;

  return (
    <>
      {getActualChecks && (
        <ModalWindow
          title={<Header4Text>Match an Actual Check Deposit</Header4Text>}
          onClose={onClose}
          width={`90vw`}
          footer={
            <FlexRow justifyContent={'flex-end'}>
              <Grid columnCount={2} justify-self="end" columnGap={'spacing3'}>
                <Button intent={'ghost'} onClick={onClose} data-test-id={'cancel-button'}>
                  Cancel
                </Button>
                <Button
                  intent={'primary'}
                  disabled={!selectedCheck}
                  onClick={onMatch}
                  data-test-id={'check-match-button'}
                >
                  Match
                </Button>
              </Grid>
            </FlexRow>
          }
        >
          <PageLoading isLoading={isLoadingGetActualChecks}>
            {!isQueryValid && (
              <ErrorMessage
                message={`The amount of the expected deposit must be less than the remaining amount of the incoming transaction.`}
              />
            )}
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={convertToError(getActualChecksError) || convertToError(matchError)}
            />
            <MatchActualCheckDepositTable
              data={getActualChecks}
              isQueryFetching={isLoadingGetActualChecks}
              query={queryObject}
              setQuery={setQueryObject}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
            />
          </PageLoading>
        </ModalWindow>
      )}
    </>
  );
};
